import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { fetchTicketSuggestions } from "./api";
import { Textarea } from "ui/components/textarea";
import { Button } from "ui";
import { Icon } from "components";
import { LOADER2 } from "icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { EvtHeader } from "components/header";

const SupportHelp = () => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = React.useState("");
  const [triggeredQuery, setTriggeredQuery] = React.useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["ticket-suggestions-help", triggeredQuery],
    queryFn: () =>
      fetchTicketSuggestions({
        inputText: triggeredQuery
      }),
    enabled: !!triggeredQuery.trim(),
  });

  const suggestionText = typeof data === "string" ? data : data?.data || "";

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim()) {
        setTriggeredQuery(inputValue);
      }
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <EvtHeader title="Help" info="Referenced from Everest documentation" />
      <div className="px-4 flex flex-col gap-1.5 items-center justify-center">
        <Textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your question and press Enter..."
          className="flex-1 min-h-20 resize-y"
          autoFocus
        />
        <p className="text-xs text-muted-foreground">
          If helper cannot resolve your issue, you can{" "}
          <Link to="../+" className="text-primary hover:underline font-medium">
            create a support ticket
          </Link>
          .
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground px-4">
          <Icon icon={LOADER2} className="animate-spin size-4" />
          <span>Searching...</span>
        </div>
      )}

      {error && (
        <div className="p-4 rounded bg-red-50 text-red-600 text-sm border border-red-100 px-4">
          {error?.message || String(error)}
        </div>
      )}

      {triggeredQuery && !isLoading && !error && (
        <div className="space-y-6 animate-in fade-in duration-300 p-4">
          {suggestionText ? (
            <div className="prose prose-sm dark:prose-invert max-w-none text-foreground/90 leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {suggestionText}
              </ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              No matching solutions found. Please proceed to create a support ticket.
            </p>
          )}

          <div className="mt-8 pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-sm font-medium text-muted-foreground">
              Did we resolve your issue?
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="w-24 cursor-pointer"
              >
                Yes
              </Button>
              <Button
                variant="default"
                onClick={() => navigate("../+")}
                className="w-24 cursor-pointer"
              >
                No
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportHelp;
